/*
 * Firebase Cloud Messaging for the console, loaded on demand.
 *
 * Nothing here imports `firebase` statically. The SDK is fetched only once a signed-in staff
 * member's browser has granted notification permission, so the initial bundle -- which every
 * page load pays for, including the login screen -- never carries it. Same Firebase project
 * (mintly-web) and VAPID key as the main site (mintly-ui src/lib/firebase.ts and
 * src/lib/push/firebaseMessaging.ts): the notification service sends with one firebase-admin
 * credential for both. This is Firebase's public web config, not a secret.
 */

export const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyABGAq26vNW7DFm5eqkcauSzgRiqJwjtMM',
  authDomain: 'mintly-web.firebaseapp.com',
  projectId: 'mintly-web',
  storageBucket: 'mintly-web.firebasestorage.app',
  messagingSenderId: '305121006970',
  appId: '1:305121006970:web:0f837347e9841736fb32c3',
};

export const VAPID_KEY =
  'BGQhbdbjzpMiwOYUn1b1jtLwY3xP1DZBUNAjLeDD-VTwUUoY2DOlXO2kE8tLhlfY_nNbhlcenK4KKHhPggcu4e8';

/**
 * Vite's production flag. Read through a cast because this tsconfig does not pull in vite/client's
 * types; Vite still replaces the expression at build time.
 */
const IS_PROD = (import.meta as unknown as { env?: { PROD?: boolean } }).env?.PROD === true;

/** frontend/src/public/firebase-messaging-sw.js, served from the root so its scope is `/`. */
export const PUSH_SW_URL = '/firebase-messaging-sw.js';

/** The modular SDK and a messaging instance, or `messaging: null` where FCM is unsupported. */
export async function loadFirebaseMessaging() {
  const [appSdk, messagingSdk] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  const app = appSdk.getApps().length ? appSdk.getApp() : appSdk.initializeApp(FIREBASE_CONFIG);
  const supported = await messagingSdk.isSupported().catch(() => false);
  return {
    getToken: messagingSdk.getToken,
    deleteToken: messagingSdk.deleteToken,
    onMessage: messagingSdk.onMessage,
    messaging: supported ? messagingSdk.getMessaging(app) : null,
  };
}

/**
 * Our worker's registration, registering it when asked to and allowed to.
 *
 * Production builds only, as on the main site: the dev server has no reason to hold a worker,
 * and a worker left behind by one outlives the session that made it. It never awaits
 * `navigator.serviceWorker.ready`, which does not settle on a page with no registration and
 * would hang whatever waited on it.
 */
export async function getPushRegistration({
  register = false,
}: { register?: boolean } = {}): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) return existing;
    if (!register || !IS_PROD) return undefined;
    return await navigator.serviceWorker.register(PUSH_SW_URL, { scope: '/' });
  } catch (err) {
    console.warn('[push] service worker registration failed', err);
    return undefined;
  }
}

/**
 * Called once at startup. Registers (or refreshes) the worker only when Chrome has already granted
 * permission: before that it has nothing to deliver, and registering it for every visitor would
 * put a gstatic fetch behind the login screen for nothing.
 */
export function registerPushWorkerIfGranted(): void {
  if (!IS_PROD) return;
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (window.Notification.permission !== 'granted') return;
  const start = () => { void getPushRegistration({ register: true }); };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
